import { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch } from '@headlessui/react';
import moment from 'moment';
import { campaignPurchaseRefType } from 'views/Invest/types';
import { CloseBlackIcon, CompactIcon, DownloadIcon, FilterIcon, ThreeDots } from '../../assets/icons';
import { Button, Dropdown } from '..';
import { TableColumnType } from '../Table';
import { buildDisplayText, sortItems } from '../../utils';
import BitSelector from '../BitSelectorProp';
import SlideOutPanel from '../SlideOutPanel';
import ExportCSV from '../ExportToCsv';
import ExpandingSearch, { processSearch } from '../ExpandingSearch';
import { DropdownItem } from '../Dropdown';
import { DATA_REFS, Product } from '../../types/productType';
import { PDFilters, DDFilterItem, Filters, FilterTypes } from '../Filters';
import { SortByOption, SortByType } from '../../views/Portfolio/types';
import { onExportToXlsx } from '../../helpers';

export interface SortAndFilterLayoutContextType {
  config: SlideoutConfigType;
  updateSlideoutConfig: (config: Record<string, unknown>) => void;
  openSlideout: (product: Product, config?: Record<string, unknown>) => void;
  openSlideoutWithProcessor: (processor: () => Promise<Product>) => void;
  selectedProduct?: Product;
  cardLayoutConfig?: {
    show: boolean;
    isCompact: boolean;
  };
}

export const SortAndFilterLayoutContext = createContext<SortAndFilterLayoutContextType | undefined>(undefined);

enum SortDirection {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}
enum SlideOutPanelViews {
  PRODUCT_DETAILS = 'product',
  FILTERS = 'filters',
}
const searchConfig = {
  uniqueIdRef: null,
  props: [DATA_REFS.NAME, DATA_REFS.VINTAGE],
  customFindFn: (data: Product[], value: string) => {
    return [...data].filter((x) => {
      return (
        `${x[DATA_REFS.SANITIZED_WINE_NAME]}`.toLowerCase().startsWith(value.toLowerCase()) ||
        `${x[DATA_REFS.VINTAGE]} ${x[DATA_REFS.SANITIZED_WINE_NAME]}`.toLowerCase().startsWith(value.toLowerCase())
      );
    });
  },
};

const DisplayTextKeys = {
  CLEAR_FILTER: 'portfolio:clearFilter',
  RESULTS: 'common:results',
  APPLY: 'common:apply',
  [SortDirection.ASCENDING]: 'common:ascending',
  [SortDirection.DESCENDING]: 'common:descending',
  SHOW_MORE: 'common:showMore',
  ORDER: 'common:order',
  SHOW_COMPACT_LIST: 'common:showCompactList',
  EXPORT_TO_XLS: 'common:exportToXls',
  SORTBY: 'portfolio:sortBy',
  SEARCH_PLACEHOLDER_TEXT: 'portfolio:searchPlaceholderText',
  SLIDE_OUT_WINE_DETAILS_TITLE: 'portfolio:wineDetails.title',
  SLIDE_OUT_FILTER_TITLE: 'portfolio:filters.title',
  CARD_VIEW: 'portfolio:filters.card-view',
  TABLE_VIEW: 'portfolio:filters.table-view',
};

interface TabFilters {
  selections: Filters | undefined;
  data: Product[] | null;
}
export interface TabState {
  id: string;
  sortTableBy: SortByType;
  searchQuery: string;
  isCardView: boolean;
  isCompact: boolean;
  filters: TabFilters | null;
  timestamp: number;
}

interface SlideoutConfigType {
  showBackButton: boolean;
  open: boolean;
  view: SlideOutPanelViews;
  timestamp: number;
  title: string;
  onBack?: () => void;
  isDetailsFetched?: boolean;
}

enum MyPortfolioEvent {
  FIND = 'find',
}

interface SortAndFilterLayoutProp {
  products: Product[];
  children: ReactNode;
  head?: () => ReactNode;
  slideoutContent?: (
    selectedProduct: Product,
    timeStamp: number,
    onClose?: () => void,
    setTitle?: (title: string) => void,
    isDetailsFetched?: boolean,
  ) => ReactNode;
  onFilter?: (results: Product[], currentTabState?: TabState | null) => void;
  tabState?: TabState | null;
  compId?: string;
  columns?: TableColumnType[];
  sortByOptions?: SortByOption[];
  defaultSortBy?: SortByType;
  filterPanelContainerClassName?: string;
  isCardViewDefault?: boolean;
  showCompactSwitch?: boolean;
  moreSelectorTemplate?: (() => ReactNode) | null;
  filterConfigure?: (filters: DDFilterItem[]) => DDFilterItem[];
  filterOverrides?: Partial<Record<FilterTypes, unknown>>;
  loading?: boolean;
  searchQueryIn?: string;
  disableSort?: boolean;
  disableExport?: boolean;
  selectedPortfolioId?: number | string | null;
  /** Total count for server-side pagination (overrides calculated count from products array) */
  totalCount?: number;
  /** Extra control rendered inline in the toolbar (e.g. a per-case / totals view toggle). */
  toolbarExtra?: ReactNode;
}

const SortAndFilterLayout: FC<SortAndFilterLayoutProp> = ({
  products,
  children,
  head,
  slideoutContent,
  onFilter,
  tabState,
  compId,
  columns,
  sortByOptions,
  defaultSortBy,
  filterPanelContainerClassName,
  showCompactSwitch = false,
  isCardViewDefault = false,
  moreSelectorTemplate = null,
  filterConfigure,
  filterOverrides,
  loading = true,
  searchQueryIn = '',
  disableSort,
  disableExport,
  selectedPortfolioId,
  totalCount,
  toolbarExtra,
}) => {
  const { t } = useTranslation();
  const defaultTableSortingOptions = () => {
    const { id, text } = (sortByOptions || []).find((x) => x.id === DATA_REFS.DEAL_DATE)!;
    return {
      id,
      text,
      direction: SortDirection.ASCENDING,
    };
  };
  const [tmpTableSortOrderBy, setTmpTableSortOrderBy] = useState<SortByType>(
    defaultSortBy || defaultTableSortingOptions(),
  );
  const [showSortByOptions, setShowSortByOptions] = useState(false);
  const [sortTableBy, setSortTableBy] = useState<SortByType>(defaultSortBy || defaultTableSortingOptions());

  const [currentSelectedPortfolioId, setCurrentSelectedPortfolioId] = useState(selectedPortfolioId);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCardView, setIsCardView] = useState(isCardViewDefault);
  const [isCompact, setIsCompact] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<TabFilters | null>();
  const [currentTabState, setCurrentTabState] = useState<TabState | null>();

  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'common', t), [t]);

  // Keep selectedProduct in sync with latest data from products array
  // This ensures the slideout gets updated data when products are enriched async
  useEffect(() => {
    if (selectedProductId && products.length > 0) {
      const latestProduct = products.find(
        (p) => p.lwin18 === selectedProductId || p.id === selectedProductId || p.holdingId === selectedProductId,
      );
      if (latestProduct) {
        setSelectedProduct((prev) => {
          // Only update if the product has new data (e.g., quality metrics, reviews)
          const hasNewData =
            (latestProduct.wa && !prev?.wa) ||
            (latestProduct.grade && !prev?.grade) ||
            (latestProduct.liquidityScore && !prev?.liquidityScore) ||
            (latestProduct.wineAdvocateReview && !prev?.wineAdvocateReview) ||
            (latestProduct.vinousReview && !prev?.vinousReview) ||
            (latestProduct.marketPrice && !prev?.marketPrice);
          if (hasNewData) {
            return { ...prev, ...latestProduct };
          }
          return prev;
        });
      }
    }
  }, [selectedProductId, products]);
  const [slideOutPanelConfig, setSlideOutPanelConfig] = useState<SlideoutConfigType>({
    showBackButton: false,
    open: false,
    view: SlideOutPanelViews.PRODUCT_DETAILS,
    timestamp: Date.now(),
    title: '',
    isDetailsFetched: false,
  });

  const [filteredViewData, setFilteredViewData] = useState<Product[] | undefined>();
  const openSlideout = (product: Product, config?: Record<string, unknown>) => {
    setSelectedProduct({ ...product! });
    // Track product ID for syncing with latest data
    setSelectedProductId(product.lwin18 || product.id || product.holdingId || null);
    setSlideOutPanelConfig({
      open: true,
      title: displayText[DisplayTextKeys.SLIDE_OUT_WINE_DETAILS_TITLE],
      view: SlideOutPanelViews.PRODUCT_DETAILS,
      timestamp: Date.now(),
      showBackButton: false,
      isDetailsFetched: false,
      ...(config || {}),
    });
  };

  const updateSlideoutConfig = (config: Record<string, unknown>) => {
    setSlideOutPanelConfig({
      ...slideOutPanelConfig,
      ...(config || {}),
    });
  };

  const closeSlideout = () => {
    setSlideOutPanelConfig({ ...slideOutPanelConfig, open: false });
    setSelectedProductId(null);
  };

  const openSlideoutWithProcessor = (processor: () => Promise<Product>) => {
    if (processor)
      processor().then((product) => {
        setSelectedProduct({ ...product! });
        // Track product ID for syncing with latest data
        setSelectedProductId(product.lwin18 || product.id || product.holdingId || null);
        setSlideOutPanelConfig({
          showBackButton: false,
          open: true,
          title: displayText[DisplayTextKeys.SLIDE_OUT_WINE_DETAILS_TITLE],
          view: SlideOutPanelViews.PRODUCT_DETAILS,
          timestamp: Date.now(),
          isDetailsFetched: true,
        });
      });
  };

  const onCloseSortTableByPanel = () => {
    setTmpTableSortOrderBy({ ...sortTableBy });
    setShowSortByOptions(false);
  };
  const onSortTableByChange = (item: DropdownItem) => {
    setTmpTableSortOrderBy({
      ...tmpTableSortOrderBy,
      id: item.id,
      text: item.text!,
    });
  };

  const updateViewDatasource = (sortArg: SortByType, data?: Product[], isSearch = false) => {
    if (loading) return;
    let sortedTableData: Product[] = [];

    if (!disableSort) {
      sortedTableData = sortItems<Product>(
        data || filteredViewData || products,
        SortDirection.ASCENDING === sortArg.direction,
        sortArg.id as keyof Product,
      );
    } else {
      sortedTableData = data || filteredViewData || products;
    }

    if (!isSearch) {
      sortedTableData = processSearch(
        sortedTableData,
        searchConfig.props,
        searchQuery,
        searchConfig.uniqueIdRef,
        searchConfig.customFindFn,
      );
    }

    setFilteredViewData(sortedTableData);
  };
  const onApplyTableSortingFilter = () => {
    setSortTableBy({ ...tmpTableSortOrderBy });
    setShowSortByOptions(false);
    updateViewDatasource({ ...tmpTableSortOrderBy }, [...(currentFilters?.data || products)]);
  };
  const toggleSortByVisibility = () => {
    const isShow = !showSortByOptions;
    if (!isShow) onCloseSortTableByPanel();
    else setShowSortByOptions(true);
  };

  const onOpenFiltersTray = () => {
    setSlideOutPanelConfig({
      showBackButton: false,
      open: true,
      title: displayText[DisplayTextKeys.SLIDE_OUT_FILTER_TITLE],
      view: SlideOutPanelViews.FILTERS,
      timestamp: Date.now(),
      isDetailsFetched: false,
    });
  };

  const onClearFilter = () => {
    updateViewDatasource(sortTableBy, [...products]);
    setCurrentFilters({ selections: undefined, data: null });
  };

  const onShowMoreOpen = () => {};
  const onApplyFilter = (filterResults: Product[], filters?: Filters) => {
    updateViewDatasource(sortTableBy, [...filterResults]);
    setCurrentFilters({ selections: filters, data: filterResults });
    closeSlideout();
  };

  const openSideoutSidePanel = useCallback(
    (
      sProduct: Product,
      timeStamp: number,
      onClose?: () => void,
      setTitle?: (title: string) => void,
      isDetailsFetched = false,
    ) => slideoutContent?.(sProduct, timeStamp, onClose, setTitle, isDetailsFetched),
    [slideoutContent],
  );

  const onSearch = (results: Product[], query?: string) => {
    setSearchQuery(query || '');
    updateViewDatasource(sortTableBy, [...results], true);
  };

  useEffect(() => {
    updateViewDatasource(sortTableBy, [...(currentFilters?.data || products)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    setCurrentTabState({
      id: compId || '',
      sortTableBy,
      searchQuery,
      isCardView,
      isCompact,
      filters: {
        selections: currentFilters?.selections,
        data: filteredViewData || null,
      },
      timestamp: Date.now(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredViewData]);

  useEffect(() => {
    if (!loading && onFilter) {
      onFilter(currentTabState?.filters?.data || products, currentTabState || null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTabState]);

  useEffect(() => {
    let updatedState = tabState;
    if (!tabState) {
      updatedState = {
        id: compId || '',
        sortTableBy: defaultSortBy || defaultTableSortingOptions(),
        searchQuery: '',
        isCardView: isCardViewDefault,
        isCompact: false,
        filters: null,
        timestamp: Date.now(),
      };
    }

    const newSort = updatedState?.sortTableBy || defaultSortBy || defaultTableSortingOptions();
    setSortTableBy(newSort);
    setSearchQuery(updatedState?.searchQuery || '');
    setIsCardView(updatedState?.isCardView || isCardViewDefault);
    setIsCompact(updatedState?.isCompact || false);
    setCurrentFilters(updatedState?.filters);
    setTmpTableSortOrderBy(updatedState?.sortTableBy || defaultSortBy || defaultTableSortingOptions());
    updateViewDatasource(newSort, [...(updatedState?.filters?.data || products)]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabState, compId]);

  useEffect(() => {
    if (searchQueryIn) setSearchQuery(searchQueryIn);
  }, [searchQueryIn]);

  useEffect(() => {
    const hashValue = window.location.hash;
    const isFindEvent = Boolean(hashValue && hashValue.startsWith(`#${MyPortfolioEvent.FIND}=`));
    if (!isFindEvent) return;
    const ref = atob(decodeURIComponent(hashValue.replace(`#${MyPortfolioEvent.FIND}=`, '')));
    if (ref) {
      const wineInfo = JSON.parse(ref) as campaignPurchaseRefType;
      setSearchQuery(wineInfo.name ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.hash, setSearchQuery]);

  useEffect(() => {
    if (currentSelectedPortfolioId !== selectedPortfolioId) {
      onClearFilter();
      setCurrentSelectedPortfolioId(selectedPortfolioId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolioId, currentSelectedPortfolioId]);

  // Calculate loaded count from filtered data
  const loadedCount = (filteredViewData || []).length;
  // Use totalCount prop for server-side pagination when available
  const totalResults = totalCount ?? loadedCount;
  return (
    <SortAndFilterLayoutContext.Provider
      value={{
        config: slideOutPanelConfig,
        updateSlideoutConfig,
        openSlideout,
        openSlideoutWithProcessor,
        selectedProduct,
        cardLayoutConfig: { show: isCardView, isCompact: !showCompactSwitch ? true : isCompact },
      }}
    >
      {head && head()}
      <>
        <div className={`${filterPanelContainerClassName || ''}`.trim()}>
          {/* Sort and Filter Controls */}
          <div
            className={`flex items-center gap-3 ${
              products.length === 0 ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
            }`.trim()}
          >
            {/* Results Count (inline, left side — saves a full toolbar row of vertical space) */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {totalCount !== undefined ? loadedCount.toLocaleString() : totalResults.toLocaleString()}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {totalCount !== undefined
                  ? `of ${totalResults.toLocaleString()} ${displayText[DisplayTextKeys.RESULTS]}`
                  : displayText[DisplayTextKeys.RESULTS]}
              </span>
              {currentFilters?.selections && (
                <button
                  onClick={onClearFilter}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium text-orange dark:text-teal-400 hover:text-white hover:bg-orange dark:hover:bg-teal-500 rounded-lg transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {displayText[DisplayTextKeys.CLEAR_FILTER]}
                </button>
              )}
            </div>

            {/* Sort Dropdown */}
            {!disableSort && (
              <Dropdown
                usePortal={true}
                autoClose={false}
                open={showSortByOptions}
                value={tmpTableSortOrderBy.id}
                valueTemplate={
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-500 dark:text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{`${sortTableBy.text}`}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{`(${
                      displayText[DisplayTextKeys[sortTableBy.direction]]
                    })`}</span>
                  </div>
                }
                onOpen={toggleSortByVisibility}
                onItemSelect={onSortTableByChange}
                items={sortByOptions}
                itemsContainerClassName="max-h-[280px] overflow-y-auto bg-white dark:bg-slate-800 scrollbar-thin"
                itemClassName="py-3 px-4 text-sm text-gray-700 dark:text-gray-200 hover:bg-orange/5 dark:hover:bg-teal-500/10 transition-colors"
                className="px-3 py-2 rounded-xl bg-gray-100/80 dark:bg-slate-800/80 hover:bg-gray-200/80 dark:hover:bg-slate-700/80 transition-all duration-200"
                header={
                  <div className="flex flex-col relative bg-white dark:bg-slate-800">
                    <button
                      onClick={onCloseSortTableByPanel}
                      className="absolute right-3 top-3 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <CloseBlackIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                    <BitSelector
                      title={displayText[DisplayTextKeys.ORDER]}
                      leftText={displayText[DisplayTextKeys[SortDirection.ASCENDING]]}
                      rightText={displayText[DisplayTextKeys[SortDirection.DESCENDING]]}
                      isTrue={tmpTableSortOrderBy.direction === SortDirection.ASCENDING}
                      onClick={(isAsc) => {
                        setTmpTableSortOrderBy({
                          ...tmpTableSortOrderBy,
                          direction: isAsc ? SortDirection.ASCENDING : SortDirection.DESCENDING,
                        });
                      }}
                    />
                    <div className="border-t border-gray-100 dark:border-slate-700 px-4 py-3">
                      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        {displayText[DisplayTextKeys.SORTBY]}
                      </span>
                    </div>
                  </div>
                }
                footer={
                  <div className="p-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50">
                    <Button
                      className="btn w-full text-sm font-semibold bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 rounded-xl py-2.5 text-white shadow-md shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-lg hover:shadow-orange/30 dark:hover:shadow-teal-500/30 transition-all duration-200"
                      onClick={onApplyTableSortingFilter}
                      props={{
                        name: 'viewPortfolio',
                      }}
                    >
                      {displayText[DisplayTextKeys.APPLY]}
                    </Button>
                  </div>
                }
              />
            )}

            {/* Right Side Controls */}
            {!slideOutPanelConfig.open && (
              <div
                className={`flex-1 flex justify-end items-center gap-2 ${
                  products.length === 0 ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
                }`.trim()}
              >
                {/* Extra toolbar control (e.g. per-case / totals view toggle).
                    Inline on desktop; on mobile it drops to its own row below (see below). */}
                {toolbarExtra && <div className="hidden md:flex items-center">{toolbarExtra}</div>}

                {/* Search */}
                <ExpandingSearch
                  uniqueIdRef={searchConfig.uniqueIdRef}
                  placeholder={displayText[DisplayTextKeys.SEARCH_PLACEHOLDER_TEXT]}
                  data={currentFilters?.data || products}
                  props={searchConfig.props}
                  onSearch={onSearch}
                  query={searchQuery}
                  customFindFn={searchConfig.customFindFn}
                  className="focus:bg-white dark:focus:bg-slate-700 focus:shadow-md focus:shadow-gray-200/50 dark:focus:shadow-slate-900/50 dark:bg-slate-800 dark:text-white dark:placeholder-gray-400 transition-shadow"
                />

                {/* Filter Button */}
                <button
                  onClick={() => onOpenFiltersTray()}
                  className="relative p-2.5 rounded-xl bg-gray-100/80 dark:bg-slate-800/80 hover:bg-gray-200/80 dark:hover:bg-slate-700/80 transition-all duration-200 group"
                >
                  <FilterIcon className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors" />
                  {currentFilters?.selections && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange/60 dark:bg-teal-400/60 opacity-75" />
                      <span className="relative inline-flex rounded-full h-4 w-4 bg-orange dark:bg-teal-400 items-center justify-center">
                        <span className="text-[10px] font-bold text-white">!</span>
                      </span>
                    </span>
                  )}
                </button>

                {/* More Options */}
                {moreSelectorTemplate && moreSelectorTemplate()}
                {!moreSelectorTemplate && (
                  <Dropdown
                    onOpen={onShowMoreOpen}
                    usePortal={true}
                    containerClassName=""
                    itemsWrapperClassName="bg-white dark:bg-slate-800 shadow-xl dark:shadow-slate-900/50 rounded-2xl border border-gray-100 dark:border-slate-700 overflow-hidden min-w-[220px]"
                    itemClassName="py-4 text-sm text-gray-700 dark:text-gray-200"
                    className="p-2.5 rounded-xl bg-gray-100/80 dark:bg-slate-800/80 hover:bg-gray-200/80 dark:hover:bg-slate-700/80 transition-all duration-200"
                    header={
                      <div className="flex flex-col bg-white dark:bg-slate-800">
                        <div className="p-4 border-b border-gray-100 dark:border-slate-700">
                          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            {displayText[DisplayTextKeys.SHOW_MORE]}
                          </span>
                        </div>
                        <BitSelector
                          title=""
                          leftText={displayText[DisplayTextKeys.CARD_VIEW]}
                          rightText={displayText[DisplayTextKeys.TABLE_VIEW]}
                          isTrue={isCardView}
                          onClick={(isViewAsCard) => setIsCardView(isViewAsCard)}
                        />
                        {showCompactSwitch && (
                          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100 dark:border-slate-700">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700">
                                <CompactIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                {displayText[DisplayTextKeys.SHOW_COMPACT_LIST]}
                              </span>
                            </div>
                            <Switch
                              checked={isCompact}
                              onChange={(val: boolean) => setIsCompact(val)}
                              className={`${
                                isCompact ? 'bg-orange dark:bg-teal-500' : 'bg-gray-300 dark:bg-slate-600'
                              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200`}
                            >
                              <span className="sr-only">{displayText[DisplayTextKeys.SHOW_COMPACT_LIST]}</span>
                              <span
                                className={`${
                                  isCompact ? 'translate-x-6' : 'translate-x-1'
                                } inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-200`}
                              />
                            </Switch>
                          </div>
                        )}
                      </div>
                    }
                    footer={
                      !disableExport ? (
                        <ExportCSV
                          csvData={filteredViewData || []}
                          fileName={`${moment().format('YYYY-MM-DD HH:mm')}`}
                          onPrepareData={(data: Product[]) => onExportToXlsx(data, columns)}
                        >
                          <div className="px-4 py-3 flex cursor-pointer items-center gap-3 border-t border-gray-100 dark:border-slate-700 hover:bg-orange/5 dark:hover:bg-teal-500/10 transition-colors">
                            <div className="p-2 rounded-lg bg-gray-100 dark:bg-slate-700">
                              <DownloadIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                              {displayText[DisplayTextKeys.EXPORT_TO_XLS]}
                            </span>
                          </div>
                        </ExportCSV>
                      ) : null
                    }
                  >
                    <ThreeDots className="w-5 h-5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors" />
                  </Dropdown>
                )}
              </div>
            )}
          </div>

          {/* Mobile: extra toolbar control on its own row above the table (avoids cramming it
              next to search/filter/more on narrow screens). Hidden on desktop where it sits inline. */}
          {toolbarExtra && !slideOutPanelConfig.open && (
            <div className="md:hidden mt-3 flex justify-center">{toolbarExtra}</div>
          )}
        </div>
        {children}
      </>
      <SlideOutPanel
        showBackButton={slideOutPanelConfig.showBackButton}
        onBack={slideOutPanelConfig?.onBack || (() => null)}
        headClassName="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 text-gray-800 dark:text-white"
        title={slideOutPanelConfig.title}
        isOpen={slideOutPanelConfig.open}
        onClose={closeSlideout}
      >
        {slideoutContent &&
          slideOutPanelConfig.open &&
          slideOutPanelConfig.view === SlideOutPanelViews.PRODUCT_DETAILS &&
          openSideoutSidePanel(
            selectedProduct!,
            slideOutPanelConfig.timestamp,
            closeSlideout,
            (title: string) => setSlideOutPanelConfig({ ...slideOutPanelConfig, title }),
            slideOutPanelConfig?.isDetailsFetched || false,
          )}

        {slideOutPanelConfig.view === SlideOutPanelViews.FILTERS && (
          <PDFilters
            overrides={filterOverrides}
            timestamp={slideOutPanelConfig.timestamp}
            products={products}
            onApplyFilter={onApplyFilter}
            filters={currentFilters?.selections}
            filterConfigure={filterConfigure}
            parentCompId={compId}
          />
        )}
      </SlideOutPanel>
    </SortAndFilterLayoutContext.Provider>
  );
};

export default SortAndFilterLayout;
